<template>
  <div :id="'comment-' + id " class="surface-post-comment">
    <div class="avatar">
      <avatar-image v-if="!author.username"
                    :user="author"
                    width="16"
                    height="16" />
      <a v-if="author.username" target="_blank" :href="'/' + author.username">
        <avatar-image :user="author" width="16" height="16" />
      </a>
    </div>
    <div class="comment-main">
      <div class="comment-author small-type">
        <span v-if="!author.username" class="comment-author-name">{{ authorName(author) }}</span>
        <a v-if="author.username"
           :href="'/' + author.username"
           target="_blank"
           class="comment-author-name">{{ authorName(author) }}</a>
        <relative-time class="caption-type comment-author-time"
                       :datetime="comment.created_at"
                       dateformat="short" />
      </div>
      <div v-if="!isBeingEdited"
           dir="auto"
           class="comment-body"
           v-html="hyperlinkedBody" />
      <div v-if="isBeingEdited">
        <div class="textarea-wrapper">
          <multiline-input ref="editCommentInput"
                           dir="auto"
                           class="add-comment-input"
                           :value="body"
                           :newline="false"
                           :data-value="body"
                           autofocus
                           @enter="submitEdit" />
        </div>
        <div class="button-list">
          <button class="button small-button text-button"
                  @click="$emit('stop-editing')"
                  v-t>Cancel</button>
          <button type="submit"
                  class="button small-button text-button"
                  @click="submitEdit"
                  v-t>Update</button>
        </div>
      </div>
    </div>
    <div v-if="showActions" class="comment-actions">
      <button class="icon-button" @click="toggleDropdown" v-t:title="'Comment actions'">
        <base-icon size="small" name="more_vert" />
      </button>
    </div>
  </div>
</template>

<script>
import IntlMixin from 'vuemixins/intl_mixin'
import OutsideEventsMixin from 'vuemixins/outside_events_mixin'
import BaseIcon from 'vuecomponents/base_icon'
import MultilineInput from 'vuecomponents/multiline_input'
import RelativeTime from 'vuecomponents/relative_time'
import AvatarImage from 'vuecomponents/avatar_image'
import { mapState, mapGetters } from 'vuex'
import { $ } from 'bits/jquery'

export default {
  components: { BaseIcon, AvatarImage, MultilineInput, RelativeTime },
  mixins: [IntlMixin, OutsideEventsMixin],
  props: ['id', 'comment', 'author', 'isBeingEdited'],
  computed: {
    ...mapState(['user']),
    ...mapGetters(['canIModerate']),
    body () { return this.comment.body },
    hyperlinkedBody () {
      return this.body
      // Autolink is seriously slowing the padlet down since vue is calling this
      // every time there is changes to comments
      // TODO: Move to backbone and check the rendering it speeds up (test on
      // Thanksgiving padlet)
      // return autolinker.link(this.body)
    },
    showActions () {
      return (this.user.id === this.comment.user.id || this.canIModerate) && !this.isBeingEdited
    }
  },
  updated () {
    if (this.isBeingEdited) {
      this.$refs.editCommentInput.focus()
    }
  },
  methods: {
    authorName (author) {
      return author.name || author.username || this.__('Anonymous')
    },
    toggleDropdown () {
      this.$emit('show-comment-action-menu')
    },
    onEscape () {
      if (this.isBeingEdited) {
        this.$emit('stop-editing')
      }
    },
    onOutsideClick () {
      if (this.isBeingEdited) {
        this.$emit('stop-editing')
      }
    },
    submitEdit () {
      let newBody = this.$refs.editCommentInput.$el.value
      if (newBody || newBody.trim() === '') {
        this.$emit('edit', {
          id: this.id,
          body: newBody
        })
      }
    },
  },
}
</script>

<style lang="scss">
@import "./../../assets/stylesheets/3/modules/all";
.surface-post-comment {
  position: relative;
  padding: 6px 12px;
  display: flex;
  align-items: flex-start;
  font-size: 12px;
  line-height: 17px;
  min-height: 40px;

  .comment-author {
    .comment-author-name {
      font-weight: 500;
    }

    relative-time {
      margin-left: 0.5em;
      font-size: 11px;
    }
  }

  .comment-main {
    flex-grow: 1;
  }

  .comment-body {
    word-break: break-word;
    display: block;
  }

  .comment-actions {
    position: absolute;
    top: 0;
    right: 0;
    button {
      padding-right: 0;
      html[data-layout=shelf] & {
        padding-right: 8px;
      }
    }
  }

  .button-list {
    margin-top: 8px;
    justify-content: flex-end;
  }

  a.comment-author-name {
    color: inherit;

    &:hover {
      text-decoration: underline;
      color: inherit;
    }
    &:visited {
      color: inherit;
    }
  }

  .comment-dropdown-container {
    position: relative;
  }

  .dropdown {
    font-size: $body-font-size;
  }
}

.mousable .surface-post-comment {
  .comment-actions {
    display: none;
  }

  &:hover .comment-actions {
    display: block;
  }
}

.add-comment {
  padding: 4px 4px 4px 12px;
  display: flex;
  align-items: flex-start;
  min-height: 40px;
  .avatar {
    margin: 6px 0px;
  }
  .textarea-wrapper {
    margin: 4px 0px;
  }
  .icon-button {
    margin: 3px 0px;
  }
}

[data-view-mode=edit] {
  .add-comment, .comment-actions {
    visibility: hidden;
  }
}

.read-only .add-comment {
  display: none;
}
</style>
